import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card } from '@osd/ui/card';
import { Heading } from '@osd/ui/heading';
import { Stack } from '@osd/ui/stack';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@osd/ui/tabs';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/tabs — live docs for the Tabs primitive set.
 *
 * Four parts, all exported from `@osd/ui/tabs`:
 *
 *   - <Tabs>            — root provider; owns active state
 *                         (controlled via `value`/`onValueChange`,
 *                         or uncontrolled via `defaultValue`)
 *   - <TabsList>        — <div role="tablist"> holding the tabs;
 *                         carries the WAI-ARIA `aria-orientation`
 *   - <TabsTab>         — <button role="tab">; click + Enter/Space
 *                         activate; ArrowLeft/Right (or Up/Down for
 *                         vertical) move focus between tabs
 *   - <TabsPanel>       — <div role="tabpanel">; always present in
 *                         the DOM, hidden via the `hidden` HTML
 *                         attribute when not active
 *
 * The design system stays router-agnostic — consumers using
 * client-side routing wrap a router `<Link>` with the same
 * BEM classes (`osd-tabs__tab` + `osd-tabs__tab--active`).
 */
export const Route = createFileRoute('/ui/_layout/tabs')({
  component: TabsPage,
});

const SPECIMENS = [
  {
    id: 'overview',
    title: 'Overview',
    body: (
      <Stack gap={2}>
        <Heading level={3}>Overview</Heading>
        <Text>
          The Tabs primitive implements the WAI-ARIA Authoring Practices "tabs with manual
          activation" pattern. Keyboard nav: Arrow keys move focus; Home/End jump to first/last;
          Enter/Space activate the focused tab.
        </Text>
      </Stack>
    ),
  },
  {
    id: 'specs',
    title: 'Specs',
    body: (
      <Stack gap={2}>
        <Heading level={3}>Specs</Heading>
        <Text>
          Compound shape: Tabs / TabsList / TabsTab / TabsPanel. Each `value` ties one tab to one
          panel; the auto-generated IDs wire `aria-controls` and `aria-labelledby` for
          screen-readers.
        </Text>
      </Stack>
    ),
  },
  {
    id: 'reviews',
    title: 'Reviews',
    body: (
      <Stack gap={2}>
        <Heading level={3}>Reviews</Heading>
        <Text>
          "Manual activation" means focus moves between tabs but only activates on Enter or Space —
          keyboard users can preview a tab without changing the visible panel.
        </Text>
      </Stack>
    ),
  },
  {
    id: 'related',
    title: 'Related',
    body: (
      <Stack gap={2}>
        <Heading level={3}>Related</Heading>
        <Text>
          The sidebar (`@osd/ui/nav`) handles persistent navigation between routes; the tabs
          primitive handles transient switching between views of the same context.
        </Text>
      </Stack>
    ),
  },
] as const;

function TabsPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Horizontal tabs"
        hint="The default layout. Tabs sit in a strip with an underline; active tab gets the accent color."
      >
        <Card>
          <Card.Body>
            <Tabs defaultValue={SPECIMENS[0]?.id ?? 'overview'}>
              <TabsList>
                {SPECIMENS.map((spec) => (
                  <TabsTab key={spec.id} value={spec.id}>
                    {spec.title}
                  </TabsTab>
                ))}
              </TabsList>
              {SPECIMENS.map((spec) => (
                <TabsPanel key={spec.id} value={spec.id}>
                  {spec.body}
                </TabsPanel>
              ))}
            </Tabs>
          </Card.Body>
        </Card>
      </Section>

      <Section
        title="Vertical tabs"
        hint='Pass orientation="vertical" to lay the strip on the left with the panel on the right.'
      >
        <Card>
          <Card.Body>
            <Tabs orientation="vertical" defaultValue={SPECIMENS[0]?.id ?? 'overview'}>
              <TabsList>
                {SPECIMENS.map((spec) => (
                  <TabsTab key={spec.id} value={spec.id}>
                    {spec.title}
                  </TabsTab>
                ))}
              </TabsList>
              {SPECIMENS.map((spec) => (
                <TabsPanel key={spec.id} value={spec.id}>
                  {spec.body}
                </TabsPanel>
              ))}
            </Tabs>
          </Card.Body>
        </Card>
      </Section>

      <Section
        title="Controlled tabs"
        hint="Pass value + onValueChange to drive the active tab from outside the component."
      >
        <ControlledExample />
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Uncontrolled"
            code={`import { Tabs, TabsList, TabsPanel, TabsTab } from '@osd/ui/tabs';

<Tabs defaultValue="overview">
  <TabsList>
    <TabsTab value="overview">Overview</TabsTab>
    <TabsTab value="specs">Specs</TabsTab>
    <TabsTab value="reviews">Reviews</TabsTab>
  </TabsList>
  <TabsPanel value="overview">…</TabsPanel>
  <TabsPanel value="specs">…</TabsPanel>
  <TabsPanel value="reviews">…</TabsPanel>
</Tabs>`}
          />

          <CodeBlock
            language="tsx"
            title="Controlled (external state)"
            code={`import { useState } from 'react';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@osd/ui/tabs';

function ExternalTabs() {
  const [active, setActive] = useState('overview');
  return (
    <Tabs value={active} onValueChange={setActive}>
      <TabsList>
        <TabsTab value="overview">Overview</TabsTab>
        <TabsTab value="specs">Specs</TabsTab>
      </TabsList>
      <TabsPanel value="overview">…</TabsPanel>
      <TabsPanel value="specs">…</TabsPanel>
    </Tabs>
  );
}`}
          />

          <CodeBlock
            language="tsx"
            title="With router-aware wrapper (TanStack Router example)"
            code={`// apps/web's TabLink is a thin wrapper around TanStack
// Router's <Link> that re-uses the BEM classes from @osd/ui:
//   className="osd-tabs__tab" + "osd-tabs__tab--active"
// on the matching route segment.

import { Link } from '@tanstack/react-router';

<Link
  to="/docs/$section"
  params={{ section: 'overview' }}
  className="osd-tabs__tab"
  activeProps={{ className: 'osd-tabs__tab osd-tabs__tab--active' }}
>
  Overview
</Link>`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}

function ControlledExample(): JSX.Element {
  const [active, setActive] = useState<string>('overview');
  return (
    <Stack gap={3}>
      <Tabs value={active} onValueChange={setActive}>
        <TabsList>
          {SPECIMENS.map((spec) => (
            <TabsTab key={spec.id} value={spec.id}>
              {spec.title}
            </TabsTab>
          ))}
        </TabsList>
        {SPECIMENS.map((spec) => (
          <TabsPanel key={spec.id} value={spec.id}>
            {spec.body}
          </TabsPanel>
        ))}
      </Tabs>
      <Text muted>
        Current tab: <code>{active}</code>. Switch via the tabs above — the value is driven from a
        useState in the parent.
      </Text>
    </Stack>
  );
}
